import { baseApi } from "../api/baseApi";

/** @typedef {{ empId: number, name: string, email: string, role: string, lastLogin: string | null }} UserActivityRecentUser */
/** @typedef {{ users: UserActivityRecentUser[] }} UserActivityRecentUsersResponse */

function normalizeRecentUsersResponse(response) {
  const users = Array.isArray(response?.users) ? response.users : [];
  return users.map((row) => ({
    empId: row.empId ?? row.emp_id,
    name: String(row.name ?? "").trim(),
    email: String(row.email ?? "").trim(),
    role: String(row.role ?? "").trim(),
    lastLogin: row.lastLogin ?? row.last_login ?? null,
  }));
}

function normalizeMetadataJson(value) {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "object") {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeEventRow(row) {
  return {
    activityEventId: row.activityEventId ?? row.activity_event_id,
    empId: row.empId ?? row.emp_id ?? null,
    userName: String(row.userName ?? row.user_name ?? "").trim(),
    eventType: String(row.eventType ?? row.event_type ?? "").trim(),
    eventAt: row.eventAt ?? row.event_at ?? null,
    pagePath: String(row.pagePath ?? row.page_path ?? "").trim(),
    resourceType: String(row.resourceType ?? row.resource_type ?? "").trim(),
    resourceId: String(row.resourceId ?? row.resource_id ?? "").trim(),
    action: String(row.action ?? "").trim(),
    successFlag: row.successFlag === true || row.success_flag === true,
    metadataJson: normalizeMetadataJson(row.metadataJson ?? row.metadata_json),
  };
}

function normalizeEventsPageResponse(response) {
  const items = Array.isArray(response?.items) ? response.items.map(normalizeEventRow) : [];
  return {
    items,
    page: Number(response?.page) > 0 ? Number(response.page) : 1,
    size: Number(response?.size) > 0 ? Number(response.size) : 25,
    totalFiltered: Number(response?.totalFiltered ?? response?.total_filtered ?? 0),
  };
}

/** @param {string} dateStr YYYY-MM-DD */
export function dateToActivityFromInstant(dateStr) {
  if (!dateStr) {
    return undefined;
  }
  return `${dateStr}T00:00:00.000Z`;
}

/** End of calendar day as exclusive instant for API {@code to} param. */
export function dateToActivityToInstantExclusive(dateStr) {
  if (!dateStr) {
    return undefined;
  }
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) {
    return undefined;
  }
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString();
}

export const userActivityApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUserActivityRecent: build.query({
      query: (limit = 50) => ({
        url: "/user-activity/recent",
        method: "GET",
        params: { limit },
      }),
      transformResponse: (response) => normalizeRecentUsersResponse(response),
      providesTags: ["UserActivityRecent"],
    }),
    recordUserActivityEvent: build.mutation({
      query: (body) => ({
        url: "/user-activity/events",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UserActivityEvents"],
    }),
    getUserActivityEvents: build.query({
      query: ({ page = 1, size = 25, empId, pagePath, action, from, to } = {}) => {
        const params = { page, size };
        if (empId != null && empId !== "") {
          params.empId = Number(empId);
        }
        const pathFilter = String(pagePath ?? "").trim();
        if (pathFilter) {
          params.pagePath = pathFilter;
        }
        const actionFilter = String(action ?? "").trim();
        if (actionFilter) {
          params.action = actionFilter;
        }
        if (from) {
          params.from = from;
        }
        if (to) {
          params.to = to;
        }
        return {
          url: "/user-activity/events",
          method: "GET",
          params,
        };
      },
      transformResponse: (response) => normalizeEventsPageResponse(response),
      providesTags: ["UserActivityEvents"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserActivityRecentQuery,
  useGetUserActivityEventsQuery,
  useRecordUserActivityEventMutation,
} = userActivityApi;
